import React, { useState } from "react";
import { cn } from "@/lib/utils";
import DataSourceInfo from "@/components/DataSourceInfo";
import { MountainSnow } from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

export interface IcebergLocationData {
  iceberg_id: string;
  latitude: number;
  longitude: number;
  length_nm: number | null;
  width_nm: number | null;
  area_sqmi: number | null;
  area_sqnm: number | null;
  area_sqkm: number | null;
  last_update: string | null;
  est_mean_draft?: number | null;
  est_volume?: number | null;
  min_avg_annual_thickness_melt_rate_m_per_yr?: number | null;
  min_avg_annual_lateral_retreat_m_per_yr?: number | null;
  min_est_annual_mass_loss_Gt_per_yr?: number | null;
  melt_data_source?: string | null;
  tracking_uncertainty_percent?: number | null;
  primary_tracking_agency?: string | null;
  max_avg_annual_thickness_melt_rate_m_per_yr?: number | null;
  max_est_annual_mass_loss_Gt_per_yr?: number | null;
  max_avg_annual_lateral_retreat_m_per_yr?: number | null;
  tracking_history?: Array<{
    observation_date: string;
    area_sqkm?: number | null;
    min_est_annual_mass_loss_Gt_per_yr?: number | null;
  }> | null;
}

interface IcebergPanelProps {
  iceberg: IcebergLocationData;
}

const formatNumber = (value: number | null | undefined) =>
  value == null
    ? "—"
    : Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 });

const formatDate = (value: string | null | undefined) => {
  if (!value) return "Unknown";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const getUncertaintyBadge = (pct: number | null | undefined) => {
  if (pct == null) return null;
  if (pct <= 15)
    return (
      <span className="px-2 py-0.5 rounded-full bg-green-600/20 text-green-600 text-xs font-medium">
        Low
      </span>
    );
  if (pct <= 25)
    return (
      <span className="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-500 text-xs font-medium">
        Moderate
      </span>
    );
  return (
    <span className="px-2 py-0.5 rounded-full bg-red-500/20 text-red-500 text-xs font-medium">
      High
    </span>
  );
};

const IcebergPanel: React.FC<IcebergPanelProps> = ({ iceberg }) => {
  const [chartMetric, setChartMetric] = useState<"area" | "massloss">("area");

  const chartEntries = (iceberg.tracking_history ?? []).filter(
    (e) => e.observation_date,
  );
  const hasChart = chartEntries.length >= 2;

  const massLossValues = chartEntries
    .map((e) => e.min_est_annual_mass_loss_Gt_per_yr)
    .filter((v): v is number => v != null);
  const massLossVaries =
    massLossValues.length >= 2 &&
    Math.max(...massLossValues) - Math.min(...massLossValues) > 0.001;

  const chartData = chartEntries.map((e) => ({
    date: new Date(e.observation_date).toLocaleDateString("en-US", {
      year: "2-digit",
      month: "short",
    }),
    area: e.area_sqkm != null ? Number(e.area_sqkm) : null,
    massloss:
      e.min_est_annual_mass_loss_Gt_per_yr != null
        ? Number(e.min_est_annual_mass_loss_Gt_per_yr)
        : null,
  }));

  const meltRates = [
    {
      label: "Thickness melt rate",
      min: iceberg.min_avg_annual_thickness_melt_rate_m_per_yr,
      max: iceberg.max_avg_annual_thickness_melt_rate_m_per_yr,
      unit: "m/yr",
    },
    {
      label: "Lateral retreat",
      min: iceberg.min_avg_annual_lateral_retreat_m_per_yr,
      max: iceberg.max_avg_annual_lateral_retreat_m_per_yr,
      unit: "m/yr",
    },
    {
      label: "Annual mass loss",
      min: iceberg.min_est_annual_mass_loss_Gt_per_yr,
      max: iceberg.max_est_annual_mass_loss_Gt_per_yr,
      unit: "Gt/yr",
    },
  ];

  return (
    <div className="space-y-6">
      {/* ── Hero ── */}
      <div className="relative overflow-hidden rounded-xl border border-border bg-gradient-to-br from-background via-muted/20 to-muted/40 p-6">
        <div className="absolute inset-0 bg-grid-white/[0.02] bg-grid-16" />
        <div className="relative grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <div className="w-2 h-2 rounded-full bg-sky-500 animate-pulse" />
              Total Area
            </div>
            <div className="text-4xl text-left font-bold text-primary">
              {formatNumber(iceberg.area_sqkm)}{" "}
              <span className="font-normal text-sm">km²</span>
            </div>
            <p className="text-sm text-left text-muted-foreground">
              {formatNumber(iceberg.area_sqnm)} NM² ·{" "}
              {formatNumber(iceberg.area_sqmi)} MI²
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <div className="w-2 h-2 rounded-full bg-muted-foreground" />
              Tracking position
            </div>
            <div className="text-left text-2xl font-bold">
              {iceberg.latitude.toFixed(2)}°,{" "}
              <span>{iceberg.longitude.toFixed(2)}°</span>
            </div>
            <p className="text-sm text-left text-muted-foreground">
              Last updated {formatDate(iceberg.last_update)}
            </p>
          </div>
        </div>
      </div>

      {/* ── Dimensions ── */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Dimensions</h3>
          <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground font-semibold">
            <MountainSnow className="h-3.5 w-3.5 text-blue-500" />
            Iceberg {iceberg.iceberg_id}
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { label: "Length", value: `${formatNumber(iceberg.length_nm)} NM` },
            { label: "Width", value: `${formatNumber(iceberg.width_nm)} NM` },
          ].map(({ label, value }) => (
            <div
              key={label}
              className="group relative overflow-hidden rounded-lg border border-border bg-card p-4 hover:shadow-md transition-all duration-200 hover:border-sky-500/30"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-sky-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative flex items-center justify-between">
                <span className="font-medium">{label}</span>
                <span className="text-primary font-semibold">{value}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Volume & Depth Estimates ── */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Volume & Depth Estimates</h3>
        <div className="relative overflow-hidden rounded-xl border border-border bg-gradient-to-br from-background via-muted/20 to-muted/40 p-6">
          <div className="absolute inset-0 bg-grid-white/[0.02] bg-grid-16" />
          <div className="relative grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                Estimated Volume
              </div>
              <div className="text-4xl text-left font-bold text-cyan-400">
                {iceberg.est_volume != null
                  ? formatNumber(iceberg.est_volume)
                  : "—"}{" "}
                <span className="font-normal text-sm">km³</span>
              </div>
              <p className="text-sm text-left text-muted-foreground">
                Total estimated ice volume
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                Mean Draft
              </div>
              <div className="text-4xl text-left font-bold text-blue-500">
                {iceberg.est_mean_draft != null
                  ? formatNumber(iceberg.est_mean_draft)
                  : "—"}{" "}
                <span className="font-normal text-sm">m</span>
              </div>
              <p className="text-sm text-left text-muted-foreground">
                Average submerged depth below sea level
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Melt & Retreat Rates ── */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Annual Melt & Retreat Rates</h3>
          {iceberg.melt_data_source && (
            <span className="text-xs text-muted-foreground">
              {iceberg.melt_data_source}
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {meltRates.map(({ label, min, max, unit }, idx) => (
            <div
              key={idx}
              className="group relative overflow-hidden rounded-lg border border-border bg-card p-4 hover:shadow-md transition-all duration-200 hover:border-sky-500/30"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-sky-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative space-y-2">
                <span className="text-xs uppercase tracking-wide text-muted-foreground">
                  {label}
                </span>
                <div className="text-sm font-semibold">
                  {min != null || max != null ? (
                    <>
                      {formatNumber(min)}{" "}
                      <span className="text-muted-foreground font-normal">
                        to
                      </span>{" "}
                      {formatNumber(max)}{" "}
                      <span className="text-muted-foreground font-normal text-xs">
                        {unit}
                      </span>
                    </>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Tracking History Chart ── */}
      {hasChart && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Tracking History</h3>
            {massLossVaries && (
              <div className="flex items-center gap-1 rounded-lg border border-border bg-muted p-1">
                <button
                  onClick={() => setChartMetric("area")}
                  className={cn(
                    "px-3 py-1 rounded-md text-xs font-medium transition-colors",
                    chartMetric === "area"
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  Area
                </button>
                <button
                  onClick={() => setChartMetric("massloss")}
                  className={cn(
                    "px-3 py-1 rounded-md text-xs font-medium transition-colors",
                    chartMetric === "massloss"
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  Mass Loss
                </button>
              </div>
            )}
          </div>
          <div className="relative overflow-hidden rounded-xl border border-border bg-gradient-to-br from-background via-muted/20 to-muted/40 p-4">
            <div className="absolute inset-0 bg-grid-white/[0.02] bg-grid-16" />
            <div className="relative h-44">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={chartData}
                  margin={{ top: 4, right: 12, left: 0, bottom: 4 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="currentColor"
                    strokeOpacity={0.08}
                  />
                  <XAxis
                    dataKey="date"
                    tick={{
                      fontSize: 11,
                      fill: "hsl(var(--muted-foreground))",
                    }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    tick={{
                      fontSize: 11,
                      fill: "hsl(var(--muted-foreground))",
                    }}
                    tickLine={false}
                    axisLine={false}
                    width={chartMetric === "area" ? 60 : 44}
                    tickFormatter={(v) =>
                      chartMetric === "area"
                        ? v.toLocaleString(undefined, {
                            maximumFractionDigits: 0,
                          })
                        : v.toFixed(2)
                    }
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "0.5rem",
                      fontSize: 12,
                    }}
                    labelStyle={{
                      color: "hsl(var(--foreground))",
                      fontWeight: 600,
                    }}
                    itemStyle={{
                      color: chartMetric === "area" ? "#38bdf8" : "#f59e0b",
                    }}
                    formatter={(value: number) =>
                      chartMetric === "area"
                        ? [
                            `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })} km²`,
                            "Area",
                          ]
                        : [`${value.toFixed(3)} Gt/yr`, "Mass Loss Rate"]
                    }
                  />
                  {chartMetric === "area" ? (
                    <Line
                      type="monotone"
                      dataKey="area"
                      stroke="#38bdf8"
                      strokeWidth={2}
                      dot={{ fill: "#38bdf8", r: 3 }}
                      activeDot={{ r: 5 }}
                      connectNulls
                    />
                  ) : (
                    <Line
                      type="monotone"
                      dataKey="massloss"
                      stroke="#f59e0b"
                      strokeWidth={2}
                      dot={{ fill: "#f59e0b", r: 3 }}
                      activeDot={{ r: 5 }}
                      connectNulls
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
            <p className="mt-2 text-xs text-muted-foreground text-center">
              {chartMetric === "area"
                ? `Surface area (km²) across ${chartEntries.length} observations`
                : `Annual mass loss rate (Gt/yr) across ${chartEntries.length} observations`}
            </p>
          </div>
        </div>
      )}

      {/* ── Tracking Agency & Uncertainty ── */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Tracking</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="group relative overflow-hidden rounded-lg border border-border bg-card p-4 hover:shadow-md transition-all duration-200 hover:border-sky-500/30">
            <div className="absolute inset-0 bg-gradient-to-r from-sky-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                Primary agency
              </span>
              <span className="text-sm font-semibold">
                {iceberg.primary_tracking_agency ?? "—"}
              </span>
            </div>
          </div>

          <div className="group relative overflow-hidden rounded-lg border border-border bg-card p-4 hover:shadow-md transition-all duration-200 hover:border-sky-500/30">
            <div className="absolute inset-0 bg-gradient-to-r from-sky-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                Tracking uncertainty
              </span>
              <div className="flex items-center gap-2">
                {getUncertaintyBadge(iceberg.tracking_uncertainty_percent)}
                <span className="text-sm font-semibold">
                  {iceberg.tracking_uncertainty_percent != null
                    ? `${iceberg.tracking_uncertainty_percent}%`
                    : "—"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Data source ── */}
      <DataSourceInfo
        dataType="iceberg"
        updatedAt={iceberg.last_update ?? undefined}
      />
    </div>
  );
};

export default IcebergPanel;
